import React, { useMemo, useState } from 'react';
import { DefaultPageLayout } from '../components/layouts/DefaultPageLayout';
import { Button } from '../components/ui/button';
import { TextField } from '../components/ui/TextField';
import BunnyLoader from '../components/ui/BunnyLoader';
import { IconWithBackground } from '../components/ui/icon-with-background';
import { FeatherTrendingUp } from '../components/ui/icons';
import { financeApi } from '../services/api';
import './FinancePayslips.css';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function FinancePayoutSummary() {
  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const [year, setYear] = useState(currentYear);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const load = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await financeApi.getYearlyPayoutSummary(Number(year));
      setData(res);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || 'Failed to load yearly payout summary');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DefaultPageLayout>
      <div className="finance-payslips-container">
        <div className="header">
          <div className="header-left">
            <IconWithBackground icon={<FeatherTrendingUp />} />
            <div>
              <h1 className="text-heading-2">Payroll summary</h1>
              <p className="text-body text-subtext-color">Aggregated monthly payout totals and status for the year.</p>
            </div>
          </div>
        </div>

        {error && <div className="inline-alert">{error}</div>}

        <div className="content-card">
          <div className="setup-grid">
            <TextField label="Year">
              <TextField.Input
                type="number"
                min="1970"
                max="3000"
                step="1"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                disabled={loading}
              />
            </TextField>
            <div className="actions-row compact" style={{ marginTop: 24 }}>
              <Button variant="neutral-secondary" onClick={load} disabled={loading}>
                Load summary
              </Button>
            </div>
          </div>

          {loading && (
            <div className="py-6">
              <BunnyLoader size="medium" text="Loading summary..." animation="bounce" />
            </div>
          )}

          {!loading && data?.months?.length > 0 && (
            <div className="payout-table-wrap" style={{ marginTop: 16 }}>
              <table className="payout-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Total (INR)</th>
                    <th>Completed (INR)</th>
                    <th>Pending (INR)</th>
                    <th>Completed</th>
                    <th>Pending</th>
                    <th>Compute errors</th>
                  </tr>
                </thead>
                <tbody>
                  {data.months.map((m) => (
                    <tr key={m.month}>
                      <td>{MONTH_LABELS[m.month - 1] || m.month}</td>
                      <td>{m.total_amount == null ? '—' : Number(m.total_amount || 0).toFixed(2)}</td>
                      <td>{m.completed_amount == null ? '—' : Number(m.completed_amount || 0).toFixed(2)}</td>
                      <td>{m.pending_amount == null ? '—' : Number(m.pending_amount || 0).toFixed(2)}</td>
                      <td>
                        {m.completed_count}/{m.total_count}
                      </td>
                      <td>
                        {m.pending_count}/{m.total_count}
                      </td>
                      <td>{m.compute_errors ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && data && (!data.months || data.months.length === 0) && (
            <p className="text-body text-subtext-color" style={{ marginTop: 16 }}>
              No summary data available.
            </p>
          )}
        </div>
      </div>
    </DefaultPageLayout>
  );
}

